import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PractitionerTable from './components/PractitionerTable';
import PractitionerModal from './components/PractitionerModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import AccessLinkModal from './components/AccessLinkModal';
import SuperAdminClearDataModal from './components/SuperAdminClearDataModal';
import PaginationControls from './components/PaginationControls';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Placeholder for dataManagementService - replace with actual import path
const dataManagementService = {
  deleteAllData: async () => {
    // Implement actual deletion logic or import from appropriate service
    return {
      success: true,
      deletedReferrals: 0,
      deletedPractitioners: 0
    };
  }
};

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeView, setActiveView] = useState('overview'); // 'overview' or 'practitioners'
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // Analytics state
  const [dateRange, setDateRange] = useState(30);
  const [trendsData, setTrendsData] = useState([]);
  const [practitionerPerformance, setPractitionerPerformance] = useState([]);
  const [urgencyData, setUrgencyData] = useState([]);
  const [analyticsStats, setAnalyticsStats] = useState({
    totalReferrals: 0,
    avgResponseTime: 0,
    completionRate: 0
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal states
  const [showPractitionerModal, setShowPractitionerModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAccessLinkModal, setShowAccessLinkModal] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [showClearAllDataModal, setShowClearAllDataModal] = useState(false);
  const [clearAllDataLoading, setClearAllDataLoading] = useState(false);

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/admin-login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  // Load data based on active view
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'super_admin') {
      if (activeView === 'overview') {
        loadStatistics();
        loadRecentLogs();
        loadAnalytics();
      } else {
        loadPractitioners();
      }
    }
  }, [authLoading, userProfile, activeView, currentPage, pageSize, searchTerm, statusFilter, dateRange]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      console.log('Loading admin statistics...');
      
      // Try RPC function first
      const { data: rpcData, error: rpcError } = await supabase?.rpc('get_admin_statistics');
      
      if (rpcError) {
        console.error('RPC error:', rpcError);
        // Fallback to manual queries if RPC fails
        console.log('Falling back to manual queries...');
        
        const [practitioners, referrals, superAdmins] = await Promise.all([
          supabase?.from('practitioners')?.select('id, status', { count: 'exact' }),
          supabase?.from('referrals')?.select('id, status', { count: 'exact' }),
          supabase?.from('user_profiles')?.select('id', { count: 'exact' })?.eq('role', 'super_admin')
        ]);
        
        const practitionersList = practitioners?.data || [];
        const referralsList = referrals?.data || [];
        
        const stats = {
          total_practitioners: practitioners?.count || 0,
          active_practitioners: practitionersList?.filter(p => p?.status === 'active')?.length,
          inactive_practitioners: practitionersList?.filter(p => p?.status === 'inactive')?.length,
          total_referrals: referrals?.count || 0,
          pending_referrals: referralsList?.filter(r => r?.status === 'new')?.length,
          total_super_admins: superAdmins?.count || 0
        };
        
        console.log('Manual query statistics:', stats);
        setStatistics(stats);
      } else {
        const stats = rpcData?.[0] || null;
        console.log('RPC statistics:', stats);
        setStatistics(stats);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      // Set default values to show 0 instead of null
      setStatistics({
        total_practitioners: 0,
        active_practitioners: 0,
        inactive_practitioners: 0,
        total_referrals: 0,
        pending_referrals: 0,
        total_super_admins: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRecentLogs = async () => {
    try {
      setLogsLoading(true);
      const { data, error } = await supabase?.from('audit_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(10);
      if (error) throw error;
      setAuditLogs(data || []);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use pagination function
      const { data, error: fetchError } = await supabase?.rpc('get_practitioners_paginated', {
        p_page: currentPage,
        p_page_size: pageSize,
        p_search: searchTerm || null,
        p_status: statusFilter === 'all' ? null : statusFilter
      });

      if (fetchError) throw fetchError;
      
      if (data && data?.length > 0) {
        // Convert snake_case to camelCase
        const practitioners = data?.map(p => ({
          id: p?.id,
          slug: p?.slug,
          practiceName: p?.practice_name,
          practitionerTitle: p?.practitioner_title,
          practitionerName: p?.practitioner_name,
          email: p?.email,
          phone: p?.phone,
          address: p?.address,
          city: p?.city,
          postalCode: p?.postal_code,
          primaryColor: p?.primary_color,
          secondaryColor: p?.secondary_color,
          status: p?.status,
          referralEmail: p?.referral_email,
          createdAt: p?.created_at,
          updatedAt: p?.updated_at
        }));
        
        setPractitioners(practitioners);
        setTotalCount(data?.[0]?.total_count || 0);
      } else {
        setPractitioners([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error loading practitioners:', err);
      setError('Erreur lors du chargement des praticiens');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const startDate = subDays(new Date(), dateRange);
      
      // Fetch all referrals
      const { data: referrals, error: refError } = await supabase?.from('referrals')?.select('*')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });
      
      if (refError) throw refError;
      
      // Fetch practitioners
      const { data: practitioners, error: practError } = await supabase?.from('practitioners')?.select('id, practitioner_name, email, status');
      
      if (practError) throw practError;
      
      // Calculate statistics
      const totalReferrals = referrals?.length || 0;
      const completedReferrals = referrals?.filter(r => r?.status === 'completed')?.length || 0;
      const completionRate = totalReferrals > 0 ? Math.round((completedReferrals / totalReferrals) * 100) : 0;
      
      // Calculate average response time
      let avgResponseTime = 0;
      const referralsWithTime = referrals?.filter(r => r?.updated_at && r?.created_at);
      if (referralsWithTime?.length > 0) {
        const totalTime = referralsWithTime?.reduce((sum, r) => {
          const diff = new Date(r.updated_at) - new Date(r.created_at);
          return sum + diff;
        }, 0);
        avgResponseTime = Math.round(totalTime / referralsWithTime?.length / (1000 * 60 * 60)); // hours
      }
      
      setAnalyticsStats({
        totalReferrals,
        avgResponseTime,
        completionRate
      });
      
      // Process trends data (group by day)
      const trendsByDate = {};
      referrals?.forEach(ref => {
        const date = format(new Date(ref.created_at), 'dd/MM', { locale: fr });
        if (!trendsByDate?.[date]) {
          trendsByDate[date] = { date, total: 0, urgent: 0, normal: 0 };
        }
        trendsByDate[date].total++;
        if (ref?.urgency === 'urgent' || ref?.urgency === 'emergency') {
          trendsByDate[date].urgent++;
        } else {
          trendsByDate[date].normal++;
        }
      });
      setTrendsData(Object.values(trendsByDate));
      
      // Process practitioner performance
      const practPerformance = {};
      referrals?.forEach(ref => {
        const practId = ref?.practitioner_id;
        if (!practPerformance?.[practId]) {
          practPerformance[practId] = {
            id: practId,
            total: 0,
            pending: 0,
            completed: 0
          };
        }
        practPerformance[practId].total++;
        if (ref?.status === 'new') practPerformance[practId].pending++;
        if (ref?.status === 'completed') practPerformance[practId].completed++;
      });
      
      const practWithNames = Object.values(practPerformance)?.map(perf => {
        const pract = practitioners?.find(p => p?.id === perf?.id);
        return {
          ...perf,
          name: pract?.practitioner_name || 'Inconnu',
          email: pract?.email || ''
        };
      })?.sort((a, b) => b?.total - a?.total);
      
      setPractitionerPerformance(practWithNames);
      
      // Process urgency distribution
      const urgencyCount = {};
      referrals?.forEach(ref => {
        const urgency = ref?.urgency || 'normal';
        urgencyCount[urgency] = (urgencyCount?.[urgency] || 0) + 1;
      });
      
      setUrgencyData([
        { name: 'Normal', value: urgencyCount?.normal || 0, color: '#60a5fa' },
        { name: 'Urgent', value: urgencyCount?.urgent || 0, color: '#fbbf24' },
        { name: 'Urgence', value: urgencyCount?.emergency || 0, color: '#ef4444' }
      ]);
      
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const handleCreatePractitioner = () => {
    setModalMode('create');
    setSelectedPractitioner(null);
    setShowPractitionerModal(true);
  };

  const handleEditPractitioner = (practitioner) => {
    setModalMode('edit');
    setSelectedPractitioner(practitioner);
    setShowPractitionerModal(true);
  };

  const handleDeletePractitioner = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setShowDeleteModal(true);
  };

  const handleViewAccessLink = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setShowAccessLinkModal(true);
  };

  const handlePractitionerSaved = () => {
    setShowPractitionerModal(false);
    loadPractitioners();
  };

  const handlePractitionerDeleted = () => {
    setShowDeleteModal(false);
    loadPractitioners();
  };

  const handleClearAllData = async () => {
    try {
      setClearAllDataLoading(true);
      
      const result = await dataManagementService?.deleteAllData();
      
      if (result?.success) {
        // Reload practitioners to reflect changes
        await loadPractitioners();
        setShowClearAllDataModal(false);
        alert(
          `✅ Suppression complète réussie:\n` +
          `- ${result?.deletedReferrals} adressage(s) supprimé(s)\n` +
          `- ${result?.deletedPractitioners} praticien(s) supprimé(s)`
        );
      }
    } catch (err) {
      console.error('Error clearing all data:', err);
      alert(`❌ Erreur: ${err?.message}`);
    } finally {
      setClearAllDataLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const getActionIcon = (action) => {
    const icons = {
      CREATE: 'Plus',
      UPDATE: 'Edit',
      DELETE: 'Trash2',
      LOGIN: 'LogIn',
      LOGOUT: 'LogOut',
      CLEAR_DATA: 'Database',
      SEND_CREDENTIALS_EMAIL: 'Mail'
    };
    return icons?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colors = {
      CREATE: 'text-success',
      UPDATE: 'text-accent',
      DELETE: 'text-error',
      LOGIN: 'text-primary',
      LOGOUT: 'text-muted',
      CLEAR_DATA: 'text-warning',
      SEND_CREDENTIALS_EMAIL: 'text-blue-400'
    };
    return colors?.[action] || 'text-secondary';
  };

  const getActionLabel = (action) => {
    const labels = {
      CREATE: 'Création',
      UPDATE: 'Modification',
      DELETE: 'Suppression',
      LOGIN: 'Connexion',
      LOGOUT: 'Déconnexion',
      CLEAR_DATA: 'Nettoyage base',
      SEND_CREDENTIALS_EMAIL: 'Email envoyé'
    };
    return labels?.[action] || action;
  };

  const quickActions = [
    {
      title: 'Créer un Praticien',
      description: 'Ajouter un nouveau praticien au système',
      icon: 'UserPlus',
      color: 'accent',
      action: () => {
        setActiveView('practitioners');
        setTimeout(() => handleCreatePractitioner(), 100);
      }
    },
    {
      title: 'Gérer les Super Admins',
      description: 'Créer et gérer les comptes super admin',
      icon: 'Shield',
      color: 'purple-400',
      action: () => navigate('/super-admin-user-management')
    },
    {
      title: 'Envoyer Identifiants',
      description: 'Gérer les emails d\'identifiants',
      icon: 'Mail',
      color: 'green-400',
      action: () => navigate('/credential-management-email-system')
    },
    {
      title: 'Rapports et Analyses',
      description: 'Consulter les analyses avancées',
      icon: 'BarChart3',
      color: 'blue-400',
      action: () => navigate('/advanced-analytics-dashboard')
    },
    {
      title: 'Analytics & Audit',
      description: 'Consulter les analyses système',
      icon: 'Activity',
      color: 'blue-400',
      action: () => navigate('/advanced-system-analytics-audit-dashboard')
    }
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Super Admin Dashboard - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Shield" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">Super Admin Dashboard</h1>
                  <p className="text-sm text-muted">
                    {activeView === 'overview' ? 'Vue d\'ensemble' : `${totalCount} praticien(s) au total`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* View Toggle */}
                <div className="flex items-center gap-2 bg-card-elevated rounded-xl p-1">
                  <button
                    onClick={() => setActiveView('overview')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeView === 'overview' ?'bg-accent text-white' :'text-secondary hover:text-primary'
                    }`}
                  >
                    <Icon name="LayoutDashboard" size={18} />
                    <span className="hidden sm:inline text-sm font-medium">Vue d'ensemble</span>
                  </button>
                  <button
                    onClick={() => setActiveView('practitioners')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeView === 'practitioners' ?'bg-accent text-white' :'text-secondary hover:text-primary'
                    }`}
                  >
                    <Icon name="Users" size={18} />
                    <span className="hidden sm:inline text-sm font-medium">Praticiens</span>
                  </button>
                </div>

                <div className="flex items-center gap-3 px-4 py-2 bg-card-elevated rounded-xl">
                  <div className="w-8 h-8 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                    <Icon name="User" size={16} className="text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-primary">{userProfile?.fullName}</p>
                    <p className="text-xs text-muted">{userProfile?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeView === 'overview' ? (
            // Overview Section
            (<div className="space-y-8">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-accent/10 border border-accent/30 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                      <Icon name="Users" size={24} className="text-accent" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.total_practitioners || 0}</p>
                  <p className="text-sm text-muted">Total Praticiens</p>
                </div>
                <div className="bg-success-bg border border-success/30 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-success/20 rounded-xl flex items-center justify-center">
                      <Icon name="CheckCircle" size={24} className="text-success" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.active_practitioners || 0}</p>
                  <p className="text-sm text-muted">Praticiens Actifs</p>
                </div>
                <div className="bg-card-elevated border border-white/8 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                      <Icon name="FileText" size={24} className="text-primary" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.total_referrals || 0}</p>
                  <p className="text-sm text-muted">Total Adressages</p>
                </div>
                <div className="bg-warning-bg border border-warning/30 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-warning/20 rounded-xl flex items-center justify-center">
                      <Icon name="Clock" size={24} className="text-warning" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.pending_referrals || 0}</p>
                  <p className="text-sm text-muted">En Attente</p>
                </div>
                <div className="bg-purple-400/10 border border-purple-400/30 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-400/20 rounded-xl flex items-center justify-center">
                      <Icon name="Shield" size={24} className="text-purple-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.total_super_admins || 0}</p>
                  <p className="text-sm text-muted">Super Admins</p>
                </div>
                <div className="bg-card-elevated border border-white/8 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-muted/20 rounded-xl flex items-center justify-center">
                      <Icon name="XCircle" size={24} className="text-muted" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{statistics?.inactive_practitioners || 0}</p>
                  <p className="text-sm text-muted">Praticiens Inactifs</p>
                </div>
              </div>
              {/* Quick Actions */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                  <Icon name="Zap" size={20} className="text-accent" />
                  Actions Rapides
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {quickActions?.map((action, index) => (
                    <button
                      key={index}
                      onClick={action?.action}
                      className="bg-card-elevated hover:bg-card-hover border border-white/8 rounded-xl p-4 text-left transition-all duration-300 hover:shadow-lg hover:scale-105"
                    >
                      <div className={`w-10 h-10 bg-${action?.color}/10 rounded-lg flex items-center justify-center mb-3`}>
                        <Icon name={action?.icon} size={20} className={`text-${action?.color}`} />
                      </div>
                      <h4 className="text-sm font-semibold text-primary mb-1">{action?.title}</h4>
                      <p className="text-xs text-muted">{action?.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              {/* Recent Audit Logs */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                    <Icon name="Activity" size={20} className="text-accent" />
                    Activité Récente
                  </h3>
                  <button 
                    onClick={() => navigate('/advanced-system-analytics-audit-dashboard')}
                    className="text-sm text-accent hover:text-accent/80 transition-colors"
                  >
                    Voir tout
                  </button>
                </div>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                  </div>
                ) : auditLogs?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Inbox" size={48} className="text-muted mx-auto mb-4" />
                    <p className="text-muted">Aucune activité récente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLogs?.map((log) => (
                      <div
                        key={log?.id}
                        className="bg-card-elevated border border-white/8 rounded-xl p-4 hover:bg-card-hover transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActionColor(log?.action)} bg-current/10`}>
                            <Icon name={getActionIcon(log?.action)} size={16} className={getActionColor(log?.action)} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-semibold ${getActionColor(log?.action)}`}>
                                {getActionLabel(log?.action)}
                              </span>
                              <span className="text-xs text-muted">•</span>
                              <span className="text-xs text-muted">{log?.table_name}</span>
                            </div>
                            <p className="text-sm text-secondary mb-1">
                              Par <span className="font-medium text-primary">{log?.user_email || 'Système'}</span>
                            </p>
                            <p className="text-xs text-muted">
                              {format(new Date(log?.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Analytics Section */}
              <div className="space-y-6">
                {/* Date Range Filter */}
                <div className="bg-card rounded-xl p-4 border border-white/8">
                  <div className="flex items-center gap-4">
                    <Icon name="Calendar" size={20} className="text-muted" />
                    <span className="text-sm text-secondary">Période d'analyse:</span>
                    <div className="flex gap-2">
                      {[7, 30, 90]?.map(days => (
                        <button
                          key={days}
                          onClick={() => setDateRange(days)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            dateRange === days
                              ? 'bg-accent text-white' :'bg-white/5 text-muted hover:bg-white/10'
                          }`}
                        >
                          {days} jours
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Analytics Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-400/10 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon name="FileText" size={24} className="text-blue-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-primary mb-1">{analyticsStats?.totalReferrals}</p>
                    <p className="text-sm font-semibold text-secondary mb-1">Adressages Période</p>
                    <p className="text-xs text-muted">Sur les {dateRange} derniers jours</p>
                  </div>

                  <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-purple-400/10 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon name="Clock" size={24} className="text-purple-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-primary mb-1">{analyticsStats?.avgResponseTime}h</p>
                    <p className="text-sm font-semibold text-secondary mb-1">Temps Moyen Réponse</p>
                    <p className="text-xs text-muted">Délai de traitement</p>
                  </div>

                  <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-green-400/10 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon name="CheckCircle" size={24} className="text-green-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-primary mb-1">{analyticsStats?.completionRate}%</p>
                    <p className="text-sm font-semibold text-secondary mb-1">Taux Complétion</p>
                    <p className="text-xs text-muted">Cas traités avec succès</p>
                  </div>
                </div>

                {/* Trends Chart */}
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-400/10 flex items-center justify-center">
                      <Icon name="TrendingUp" size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">Tendances des Adressages</h3>
                      <p className="text-sm text-muted">Évolution quotidienne des soumissions</p>
                    </div>
                  </div>

                  {trendsData?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trendsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2e" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#6b7280"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                          stroke="#6b7280"
                          style={{ fontSize: '12px' }}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'rgba(26, 26, 30, 0.95)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                            color: '#e5e7eb'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="total" 
                          stroke="#60a5fa" 
                          strokeWidth={3}
                          name="Total"
                          dot={{ fill: '#60a5fa', r: 5 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="urgent" 
                          stroke="#fbbf24" 
                          strokeWidth={2}
                          name="Urgent"
                          dot={{ fill: '#fbbf24', r: 4 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="normal" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          name="Normal"
                          dot={{ fill: '#10b981', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted">
                      <p>Aucune donnée disponible pour cette période</p>
                    </div>
                  )}
                </div>

                {/* Practitioner Performance */}
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-green-400/10 flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">Performance des Praticiens</h3>
                      <p className="text-sm text-muted">Top 5 praticiens par volume</p>
                    </div>
                  </div>

                  {practitionerPerformance?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/8">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">Praticien</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Total</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">En attente</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Complétés</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Taux</th>
                          </tr>
                        </thead>
                        <tbody>
                          {practitionerPerformance?.slice(0, 5)?.map((pract, index) => (
                            <tr key={pract?.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="text-sm font-medium text-primary">{pract?.name}</p>
                                  <p className="text-xs text-muted">{pract?.email}</p>
                                </div>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-sm font-semibold text-primary">{pract?.total}</span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">
                                  {pract?.pending}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
                                  {pract?.completed}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-sm font-medium text-primary">
                                  {pract?.total > 0 ? Math.round((pract?.completed / pract?.total) * 100) : 0}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-muted">
                      <p>Aucune donnée de performance disponible</p>
                    </div>
                  )}
                </div>

                {/* Urgency Distribution */}
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-red-400/10 flex items-center justify-center">
                      <Icon name="AlertCircle" size={20} className="text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">Distribution par Urgence</h3>
                      <p className="text-sm text-muted">Répartition des cas par niveau de priorité</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {urgencyData?.map((item, index) => (
                      <div key={index} className="bg-card-elevated rounded-xl p-4 border border-white/8">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                          <p className="text-sm font-medium text-secondary">{item?.name}</p>
                        </div>
                        <p className="text-2xl font-bold text-primary">{item?.value}</p>
                        <p className="text-xs text-muted mt-1">
                          {analyticsStats?.totalReferrals > 0 ? Math.round((item?.value / analyticsStats?.totalReferrals) * 100) : 0}% du total
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>)
          ) : (
            // Practitioners Management Section
            (<>
              {/* Error Message */}
              {error && (
                <div className="mb-6 bg-error-bg border border-error/30 rounded-xl p-4 flex items-center gap-3 shadow-lg">
                  <Icon name="AlertCircle" size={20} className="text-error" />
                  <p className="text-sm text-secondary">{error}</p>
                </div>
              )}
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="Users" size={24} className="text-accent" />
                  </div>
                  <p className="text-3xl font-bold text-primary">{totalCount || 0}</p>
                  <p className="text-sm text-muted">Total Praticiens</p>
                </div>
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                  </div>
                  <p className="text-3xl font-bold text-primary">
                    {practitioners?.filter(p => p?.status === 'active')?.length || 0}
                  </p>
                  <p className="text-sm text-muted">Actifs</p>
                </div>
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="Clock" size={24} className="text-warning" />
                  </div>
                  <p className="text-3xl font-bold text-primary">
                    {practitioners?.filter(p => p?.status === 'pending')?.length || 0}
                  </p>
                  <p className="text-sm text-muted">En attente</p>
                </div>
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="XCircle" size={24} className="text-muted" />
                  </div>
                  <p className="text-3xl font-bold text-primary">
                    {practitioners?.filter(p => p?.status === 'inactive')?.length || 0}
                  </p>
                  <p className="text-sm text-muted">Inactifs</p>
                </div>
              </div>
              {/* Filters & Actions */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" />
                      <input
                        type="text"
                        placeholder="Rechercher par nom, cabinet, email..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e?.target?.value);
                          setCurrentPage(1); // Reset to first page on search
                        }}
                        className="w-full pl-10 pr-4 py-3 bg-card-elevated border border-white/8 rounded-xl text-primary placeholder-muted focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e?.target?.value);
                      setCurrentPage(1); // Reset to first page on filter
                    }}
                    className="px-4 py-3 bg-card-elevated border border-white/8 rounded-xl text-primary focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actif</option>
                    <option value="pending">En attente</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Button
                  onClick={handleCreatePractitioner}
                  className="flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white rounded-xl font-medium transition-colors shadow-lg"
                >
                  <Icon name="Plus" size={20} />
                  <span>Nouveau Praticien</span>
                </Button>
                <Button
                  onClick={() => navigate('/advanced-system-analytics-audit-dashboard')}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-xl font-medium transition-colors border border-purple-500/30"
                >
                  <Icon name="Activity" size={20} />
                  <span>Analyse & Audit</span>
                </Button>
                <Button
                  onClick={() => setShowClearAllDataModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-error/10 text-error hover:bg-error/20 rounded-xl font-medium transition-colors border border-error/30"
                >
                  <Icon name="AlertTriangle" size={20} />
                  <span>Danger Zone</span>
                </Button>
              </div>
              {/* Practitioners Table */}
              <PractitionerTable
                practitioners={practitioners}
                onEdit={handleEditPractitioner}
                onDelete={handleDeletePractitioner}
                onViewAccessLink={handleViewAccessLink}
              />
              {/* Pagination */}
              {totalCount > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>)
          )}
        </main>
      </div>
      {/* Modals */}
      {showPractitionerModal && (
        <PractitionerModal
          mode={modalMode}
          practitioner={selectedPractitioner}
          onClose={() => setShowPractitionerModal(false)}
          onSave={handlePractitionerSaved}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          practitioner={selectedPractitioner}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handlePractitionerDeleted}
        />
      )}
      {showAccessLinkModal && (
        <AccessLinkModal
          practitioner={selectedPractitioner}
          onClose={() => setShowAccessLinkModal(false)}
        />
      )}
      {showClearAllDataModal && (
        <SuperAdminClearDataModal
          isOpen={showClearAllDataModal}
          onClose={() => setShowClearAllDataModal(false)}
          onConfirm={handleClearAllData}
          loading={clearAllDataLoading}
          stats={{ referralCount: 0, practitionerCount: totalCount }}
        />
      )}
    </>
  );
};

export default SuperAdminDashboard;